import { useState } from 'react';
import { Ship, Copy, Check, ArrowRight, Users, User } from 'lucide-react';
import { createGameSession, getSessionByJoinCode, recordEvent } from '../../../lib/collaborativeGameService';
import type { SpeedBoatConfig, CollaborativeGameSession } from '../../../types/collaborativeGame';

interface SpeedBoatLiveSetupProps {
  onSessionJoined: (sessionId: string, myName: string, isHost: boolean) => void;
  onBack: () => void;
}

type SetupStep = 'choose' | 'create' | 'created' | 'join';

export function SpeedBoatLiveSetup({ onSessionJoined, onBack }: SpeedBoatLiveSetupProps) {
  const [step, setStep] = useState<SetupStep>('choose');
  const [hostName, setHostName] = useState('');
  const [topic, setTopic] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [participantName, setParticipantName] = useState('');
  const [createdSession, setCreatedSession] = useState<CollaborativeGameSession | null>(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    if (!hostName.trim()) return;
    setLoading(true);
    setError('');
    try {
      const config: SpeedBoatConfig = {
        topic: topic.trim() || undefined,
        initial_factors: [],
      };
      const session = await createGameSession('speed-boat', hostName.trim(), config);
      await recordEvent(session.id, hostName.trim(), 'join', { isHost: true });
      setCreatedSession(session);
      setStep('created');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create session');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (what: 'code' | 'link') => {
    if (!createdSession) return;
    const text = what === 'code'
      ? createdSession.joinCode
      : `${window.location.origin}${window.location.pathname}?session=${createdSession.joinCode}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleHostEnter = () => {
    if (!createdSession) return;
    onSessionJoined(createdSession.id, hostName.trim(), true);
  };

  const handleJoin = async () => {
    if (!joinCode.trim() || !participantName.trim()) return;
    setLoading(true);
    setError('');
    try {
      const session = await getSessionByJoinCode(joinCode.trim());
      if (!session) { setError('Session not found. Check the code and try again.'); return; }
      if (session.status === 'ended') { setError('This session has already ended.'); return; }
      await recordEvent(session.id, participantName.trim(), 'join', { isHost: false });
      onSessionJoined(session.id, participantName.trim(), false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join session');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'choose') {
    return (
      <div className="max-w-2xl mx-auto">
        <button onClick={onBack} className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] mb-6 flex items-center gap-1">← Back</button>
        <div className="flex items-center gap-3 mb-2">
          <Ship className="h-6 w-6 text-[var(--text-ter)]" />
          <h2 className="text-2xl font-bold text-[var(--text-pri)]">Live Speed Boat session</h2>
        </div>
        <p className="text-[var(--text-sec)] mb-8">
          Everyone places their post-its simultaneously on their own device. The order items are raised — and the conversation around them — is preserved as the session transcript.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => setStep('create')}
            className="p-6 border-2 border-[var(--rule)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-all text-left"
          >
            <div className="w-10 h-10 bg-[var(--slate-dim)] flex items-center justify-center mb-3">
              <Users className="h-5 w-5 text-[var(--text-ter)]" />
            </div>
            <h3 className="font-semibold text-[var(--text-pri)] mb-1">Host a session</h3>
            <p className="text-sm text-[var(--text-sec)]">Set the topic, get a join code, invite your group.</p>
          </button>
          <button
            onClick={() => setStep('join')}
            className="p-6 border-2 border-[var(--rule)] hover:border-[var(--green)] hover:bg-[var(--green-dim)] transition-all text-left"
          >
            <div className="w-10 h-10 bg-[var(--green-dim)] flex items-center justify-center mb-3">
              <ArrowRight className="h-5 w-5 text-[var(--green)]" />
            </div>
            <h3 className="font-semibold text-[var(--text-pri)] mb-1">Join a session</h3>
            <p className="text-sm text-[var(--text-sec)]">Enter the code the facilitator shared with you.</p>
          </button>
        </div>
      </div>
    );
  }

  if (step === 'create') {
    return (
      <div className="max-w-lg mx-auto">
        <button onClick={() => setStep('choose')} className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] mb-6 flex items-center gap-1">← Back</button>
        <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-6">Host a Speed Boat session</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Your name</label>
            <input
              type="text"
              value={hostName}
              onChange={e => setHostName(e.target.value)}
              placeholder="e.g. Alex"
              className="w-full px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Topic or question <span className="text-[var(--text-ter)] font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={topic}
              onChange={e => setTopic(e.target.value)}
              placeholder="e.g. What's slowing down our Q3 delivery?"
              className="w-full px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
            />
            <p className="text-xs text-[var(--text-ter)] mt-1">Sets context for participants when they join.</p>
          </div>
          {error && <p className="text-[var(--coral)] text-sm">{error}</p>}
          <button
            onClick={handleCreate}
            disabled={!hostName.trim() || loading}
            className="w-full px-6 py-3 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
          >
            {loading ? 'Creating…' : 'Create session'}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'created' && createdSession) {
    return (
      <div className="max-w-lg mx-auto">
        <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-2">Session ready</h2>
        <p className="text-[var(--text-sec)] mb-6">Share this code with participants, then open the board when everyone has joined.</p>
        {topic && (
          <div className="p-3 bg-[var(--surface)] border border-[var(--rule)] mb-4 text-sm text-[var(--text-sec)]">
            <span className="font-medium">Topic: </span>{topic}
          </div>
        )}
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-6 mb-4 text-center">
          <p className="text-sm text-[var(--text-sec)] font-medium mb-2">Join code</p>
          <div className="text-5xl font-bold tracking-widest text-[var(--text-sec)] font-mono mb-4">
            {createdSession.joinCode}
          </div>
          <div className="flex gap-2 justify-center">
            <button
              onClick={() => handleCopy('code')}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--bg-raised)] border border-[var(--slate)] text-sm text-[var(--text-ter)] hover:bg-[var(--slate-dim)] transition-colors"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              Copy code
            </button>
            <button
              onClick={() => handleCopy('link')}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--bg-raised)] border border-[var(--slate)] text-sm text-[var(--text-ter)] hover:bg-[var(--slate-dim)] transition-colors"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              Copy link
            </button>
          </div>
        </div>
        <button
          onClick={handleHostEnter}
          className="w-full px-6 py-3 bg-[var(--slate)] text-white hover:bg-[var(--slate)] font-medium transition-colors flex items-center justify-center gap-2"
        >
          <User className="h-5 w-5" />
          Open the board
        </button>
      </div>
    );
  }

  // Join step
  return (
    <div className="max-w-lg mx-auto">
      <button onClick={() => setStep('choose')} className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] mb-6 flex items-center gap-1">← Back</button>
      <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-6">Join a Speed Boat session</h2>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Your name</label>
          <input
            type="text"
            value={participantName}
            onChange={e => setParticipantName(e.target.value)}
            placeholder="e.g. Jordan"
            className="w-full px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Session code</label>
          <input
            type="text"
            value={joinCode}
            onChange={e => setJoinCode(e.target.value.toUpperCase())}
            placeholder="e.g. AB3KX7"
            maxLength={6}
            className="w-full px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono text-xl tracking-widest uppercase"
            onKeyDown={e => e.key === 'Enter' && handleJoin()}
          />
        </div>
        {error && <p className="text-[var(--coral)] text-sm">{error}</p>}
        <button
          onClick={handleJoin}
          disabled={!joinCode.trim() || !participantName.trim() || loading}
          className="w-full px-6 py-3 bg-[var(--green)] text-white hover:bg-[var(--green)] disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
        >
          {loading ? 'Joining…' : 'Join session'}
        </button>
      </div>
    </div>
  );
}
